import { userViewerSettingsAtom } from '@/atoms/user.atom';
import { BaseMapListItem } from '@/components/BaseMapListItem';
import { DebouncedColorInput } from '@/components/LayerSettingsDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { validateBaseMapUrl } from '@/core/baseMap';
import { ActionPosition, PointCloudPointSizeType } from '@/graphql/graphql';
import { useFormik } from 'formik';
import { useAtom } from 'jotai';
import { useFeatureFlagEnabled } from '@/lib/analytics';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface ViewerSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormValues {
  pointCloudPointSizeType: PointCloudPointSizeType;
  pointCloudPointSize: number;
  showLayerSource: boolean;
  showLayerUnderType: boolean;
  showImagePOIs: boolean;
  pointCloudPointBudget: number;
  showPointCloudBoundingBoxes: boolean;
  googleStreetViewPoiColor: string;
  actionPosition: ActionPosition;
  customBaseMaps: Array<{
    id: string;
    name: string;
    type: string;
    url: string;
    tileSize?: number | null;
    wmsVersion?: string | null;
    wmsLayers?: string | null;
    format?: string | null;
  }>;
}

export function ViewerSettingsDialog({ open, onOpenChange }: ViewerSettingsDialogProps) {
  const { t } = useTranslation('components');
  const [viewerSettings, setViewerSettings] = useAtom(userViewerSettingsAtom);
  const isGoogleStreetViewEnabled = useFeatureFlagEnabled('googleStreetview');
  const [baseMapDialogOpen, setBaseMapDialogOpen] = useState(false);
  const [editingBaseMap, setEditingBaseMap] = useState<FormValues['customBaseMaps'][number] | null>(null);

  const formik = useFormik<FormValues>({
    initialValues: {
      pointCloudPointSizeType: viewerSettings.pointCloudPointSizeType || 'ADAPTIVE',
      pointCloudPointSize: viewerSettings.pointCloudPointSize || 1.0,
      showLayerSource: viewerSettings.showLayerSource ?? false,
      showLayerUnderType: viewerSettings.showLayerUnderType ?? false,
      showImagePOIs: viewerSettings.showImagePOIs ?? true,
      pointCloudPointBudget: viewerSettings.pointCloudPointBudget || 10_000_000,
      showPointCloudBoundingBoxes: viewerSettings.showPointCloudBoundingBoxes ?? false,
      googleStreetViewPoiColor: viewerSettings.googleStreetViewPoiColor ?? '#ff69b4',
      actionPosition: viewerSettings.actionPosition || 'BOTTOM',
      customBaseMaps: (viewerSettings.customBaseMaps as any[]) ?? [],
    },
    onSubmit: async (values) => {
      setViewerSettings(values);
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] overflow-x-hidden flex flex-col'>
        <DialogHeader>
          <DialogTitle>{t('viewerSettingsDialog.title')}</DialogTitle>
          <DialogDescription>{t('viewerSettingsDialog.description')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={formik.handleSubmit} className='flex flex-col overflow-hidden'>
          <div className='grid gap-6 py-4 overflow-y-auto pr-1'>
            {/* Point Cloud Point Size */}
            <div className='space-y-3'>
              <Label className='text-base font-medium'>{t('viewerSettingsDialog.pointCloudSize')}</Label>
              <RadioGroup
                value={formik.values.pointCloudPointSizeType}
                onValueChange={(value: PointCloudPointSizeType) =>
                  formik.setFieldValue('pointCloudPointSizeType', value)
                }
                className='space-y-2'>
                <div className='flex items-center space-x-2'>
                  <RadioGroupItem value={'ADAPTIVE'} id='dynamic' />
                  <Label htmlFor='dynamic' className='cursor-pointer flex-col gap-0 items-start'>
                    <strong className='font-semibold text-base'>{t('viewerSettingsDialog.dynamic')}</strong>
                    <span className='text-xs opacity-70'>{t('viewerSettingsDialog.dynamicDescription')}</span>
                  </Label>
                </div>
                <div className='flex items-center space-x-2'>
                  <RadioGroupItem value={'FIXED'} id='fixed' />
                  <Label htmlFor='fixed' className='cursor-pointer flex-col gap-0 items-start'>
                    <strong className='font-semibold text-base'>{t('viewerSettingsDialog.fixed')}</strong>
                    <span className='text-xs opacity-70'>{t('viewerSettingsDialog.fixedDescription')}</span>
                  </Label>
                </div>
              </RadioGroup>
              {formik.values.pointCloudPointSizeType === 'FIXED' && (
                <div className='mt-4 space-y-3'>
                  <div className='flex items-center justify-between'>
                    <Label htmlFor='size' className='font-medium'>
                      {t('viewerSettingsDialog.pointSize')}
                    </Label>
                    <span className='text-xs font-medium text-muted-foreground font-mono'>
                      {formik.values.pointCloudPointSize}px
                    </span>
                  </div>
                  <Slider
                    onValueChange={(value: number[]) => formik.setFieldValue('pointCloudPointSize', value[0])}
                    value={[formik.values.pointCloudPointSize]}
                    min={1}
                    max={30}
                    step={1}
                    id='size'
                    className='w-full'
                  />
                  <div className='flex justify-between text-xs text-muted-foreground'>
                    <span>1px</span>
                    <span>30px</span>
                  </div>
                </div>
              )}
              <div className='mt-4 space-y-3'>
                <div className='flex items-center justify-between'>
                  <Label htmlFor='size' className='font-medium'>
                    {t('viewerSettingsDialog.pointBudget')}
                  </Label>
                  <span className='text-xs font-medium text-muted-foreground font-mono'>
                    {formik.values.pointCloudPointBudget.toLocaleString()} {t('viewerSettingsDialog.points')}
                  </span>
                </div>
                <Slider
                  onValueChange={(value: number[]) => formik.setFieldValue('pointCloudPointBudget', value[0])}
                  value={[formik.values.pointCloudPointBudget]}
                  min={100_000}
                  max={50_000_000}
                  step={10_000}
                  id='size'
                  className='w-full'
                />
                <div className='flex justify-between text-xs text-muted-foreground'>
                  <span>{(100_000).toLocaleString()}</span>
                  <span>{(50_000_000).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Layer Display Options */}
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <div>
                  <Label className='text-base font-medium'>{t('viewerSettingsDialog.showLayerSource')}</Label>
                  <p className='text-xs font-medium text-muted-foreground'>
                    {t('viewerSettingsDialog.showLayerSourceDescription')}
                  </p>
                </div>
                <Switch
                  checked={formik.values.showLayerSource}
                  onCheckedChange={(checked) => formik.setFieldValue('showLayerSource', checked)}
                />
              </div>

              <div className='flex items-center justify-between'>
                <div>
                  <Label className='text-base font-medium'>{t('viewerSettingsDialog.showLayerUnderType')}</Label>
                  <p className='text-xs font-medium text-muted-foreground'>
                    {t('viewerSettingsDialog.showLayerUnderTypeDescription')}
                  </p>
                </div>
                <Switch
                  checked={formik.values.showLayerUnderType}
                  onCheckedChange={(checked) => formik.setFieldValue('showLayerUnderType', checked)}
                />
              </div>

              <div className='flex items-center justify-between'>
                <div>
                  <Label className='text-base font-medium'>{t('viewerSettingsDialog.showImagePOIs')}</Label>
                  <p className='text-xs font-medium text-muted-foreground'>
                    {t('viewerSettingsDialog.showImagePOIsDescription')}
                  </p>
                </div>
                <Switch
                  checked={formik.values.showImagePOIs}
                  onCheckedChange={(checked) => formik.setFieldValue('showImagePOIs', checked)}
                />
              </div>

              <div className='flex items-center justify-between'>
                <div>
                  <Label className='text-base font-medium'>{t('viewerSettingsDialog.showBoundingBoxes')}</Label>
                  <p className='text-xs font-medium text-muted-foreground'>
                    {t('viewerSettingsDialog.showBoundingBoxesDescription')}
                  </p>
                </div>
                <Switch
                  checked={formik.values.showPointCloudBoundingBoxes}
                  onCheckedChange={(checked) => formik.setFieldValue('showPointCloudBoundingBoxes', checked)}
                />
              </div>

              {isGoogleStreetViewEnabled && (
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <DebouncedColorInput
                      id='google-street-view-poi-color'
                      className='size-8 rounded-full border-none'
                      value={formik.values.googleStreetViewPoiColor}
                      onChange={(value) => formik.setFieldValue('googleStreetViewPoiColor', value)}
                    />
                    <Label htmlFor='google-street-view-poi-color' className='text-base font-medium'>
                      {t('viewerSettingsDialog.googleStreetViewColor')}
                    </Label>
                  </div>
                </div>
              )}
            </div>

            {/* Action Position */}
            <div className='space-y-3'>
              <Label className='text-base font-medium'>{t('viewerSettingsDialog.actionPosition')}</Label>
              <p className='text-xs font-medium text-muted-foreground'>
                {t('viewerSettingsDialog.actionPositionDescription')}
              </p>
              <RadioGroup
                value={formik.values.actionPosition}
                onValueChange={(value: ActionPosition) => formik.setFieldValue('actionPosition', value)}
                className='space-y-2'>
                <div className='flex items-center space-x-2'>
                  <RadioGroupItem value={'BOTTOM'} id='action-position-bottom' />
                  <Label htmlFor='action-position-bottom' className='cursor-pointer'>
                    {t('viewerSettingsDialog.actionPositionBottom')}
                  </Label>
                </div>
                <div className='flex items-center space-x-2'>
                  <RadioGroupItem value={'RIGHT'} id='action-position-right' />
                  <Label htmlFor='action-position-right' className='cursor-pointer'>
                    {t('viewerSettingsDialog.actionPositionRight')}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Custom Base Maps */}
            <div className='space-y-3'>
              <Label className='text-base font-medium'>{t('viewerSettingsDialog.customBaseMaps')}</Label>
              {(formik.values.customBaseMaps ?? []).map((bm, index) => (
                <BaseMapListItem
                  key={bm.id || index}
                  name={bm.name}
                  type={bm.type}
                  url={bm.url}
                  onEdit={() => {
                    setEditingBaseMap(bm);
                    setBaseMapDialogOpen(true);
                  }}
                  onRemove={() => {
                    const updated = [...(formik.values.customBaseMaps ?? [])];
                    updated.splice(index, 1);
                    formik.setFieldValue('customBaseMaps', updated);
                  }}
                />
              ))}
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => {
                  setEditingBaseMap(null);
                  setBaseMapDialogOpen(true);
                }}>
                {t('viewerSettingsDialog.addBaseMap')}
              </Button>
            </div>

            <UserBaseMapDialog
              open={baseMapDialogOpen}
              onOpenChange={setBaseMapDialogOpen}
              baseMap={editingBaseMap}
              onSave={(values) => {
                if (editingBaseMap) {
                  const updated = (formik.values.customBaseMaps ?? []).map((bm) =>
                    bm.id === editingBaseMap.id ? { ...values, id: editingBaseMap.id } : bm,
                  );
                  formik.setFieldValue('customBaseMaps', updated);
                } else {
                  const entry = { ...values, id: crypto.randomUUID() };
                  formik.setFieldValue('customBaseMaps', [...(formik.values.customBaseMaps ?? []), entry]);
                }
                setBaseMapDialogOpen(false);
                setEditingBaseMap(null);
              }}
            />
          </div>
          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
              {t('viewerSettingsDialog.cancel')}
            </Button>
            <Button type='submit'>{t('viewerSettingsDialog.saveSettings')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface BaseMapValues {
  name: string;
  type: string;
  url: string;
  tileSize: number;
  wmsVersion: string;
  wmsLayers: string;
  format: string;
}

function UserBaseMapDialog({
  open,
  onOpenChange,
  baseMap,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  baseMap: FormValues['customBaseMaps'][number] | null;
  onSave: (values: BaseMapValues) => void;
}) {
  const { t } = useTranslation('components');
  const formik = useFormik<BaseMapValues>({
    initialValues: {
      name: baseMap?.name ?? '',
      type: baseMap?.type ?? 'XYZ',
      url: baseMap?.url ?? '',
      tileSize: baseMap?.tileSize ?? 256,
      wmsVersion: baseMap?.wmsVersion ?? '1.3.0',
      wmsLayers: baseMap?.wmsLayers ?? '',
      format: baseMap?.format ?? 'image/png',
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      const error = validateBaseMapUrl(values.type as any, values.url);
      if (error) {
        toast.error(error);
        return;
      }
      onSave(values);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>
            {baseMap ? t('viewerSettingsDialog.editBaseMap') : t('viewerSettingsDialog.addBaseMap')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={formik.handleSubmit}>
          <div className='grid gap-4 py-4'>
            <div className='grid gap-2'>
              <Label htmlFor='ubm-name'>{t('viewerSettingsDialog.baseMapName')}</Label>
              <Input
                id='ubm-name'
                placeholder={t('viewerSettingsDialog.namePlaceholder')}
                {...formik.getFieldProps('name')}
              />
            </div>
            <div className='grid gap-2'>
              <Label htmlFor='ubm-type'>{t('viewerSettingsDialog.baseMapType')}</Label>
              <select
                id='ubm-type'
                className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm'
                {...formik.getFieldProps('type')}>
                <option value='XYZ'>{t('viewerSettingsDialog.typeXyz')}</option>
                <option value='WMS'>{t('viewerSettingsDialog.typeWms')}</option>
                <option value='MAPBOX_STYLE'>{t('viewerSettingsDialog.typeMapboxStyle')}</option>
              </select>
            </div>
            <div className='grid gap-2'>
              <Label htmlFor='ubm-url'>{t('viewerSettingsDialog.baseMapUrl')}</Label>
              <Input
                id='ubm-url'
                placeholder={
                  formik.values.type === 'XYZ'
                    ? 'https://tile.example.com/{z}/{x}/{y}.png'
                    : formik.values.type === 'WMS'
                      ? 'https://wms.example.com/service'
                      : 'mapbox://styles/username/style-id'
                }
                {...formik.getFieldProps('url')}
              />
            </div>
            {formik.values.type === 'WMS' && (
              <>
                <div className='grid gap-2'>
                  <Label htmlFor='ubm-wms-layers'>{t('viewerSettingsDialog.wmsLayersLabel')}</Label>
                  <div className='flex gap-2'>
                    <Input
                      id='ubm-wms-layers'
                      placeholder={t('viewerSettingsDialog.wmsLayersPlaceholder')}
                      className='flex-1'
                      {...formik.getFieldProps('wmsLayers')}
                    />
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={async () => {
                        try {
                          const separator = formik.values.url.includes('?') ? '&' : '?';
                          const capsUrl = `${formik.values.url}${separator}service=WMS&request=GetCapabilities`;
                          const res = await fetch(capsUrl);
                          const text = await res.text();
                          const parser = new DOMParser();
                          const doc = parser.parseFromString(text, 'text/xml');
                          const layerEls = doc.querySelectorAll('Layer > Name');
                          const layerNames = Array.from(layerEls)
                            .map((el) => el.textContent)
                            .filter(Boolean);
                          if (layerNames.length > 0) {
                            formik.setFieldValue('wmsLayers', layerNames.join(','));
                          }
                        } catch {
                          toast.error(t('viewerSettingsDialog.failedFetchWmsAlert'));
                        }
                      }}>
                      {t('viewerSettingsDialog.fetchLayers')}
                    </Button>
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-2'>
                  <div className='grid gap-2'>
                    <Label htmlFor='ubm-wms-version'>{t('viewerSettingsDialog.wmsVersionLabel')}</Label>
                    <select
                      id='ubm-wms-version'
                      className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm'
                      {...formik.getFieldProps('wmsVersion')}>
                      <option value='1.3.0'>1.3.0</option>
                      <option value='1.1.1'>1.1.1</option>
                    </select>
                  </div>
                  <div className='grid gap-2'>
                    <Label htmlFor='ubm-format'>{t('viewerSettingsDialog.wmsFormatLabel')}</Label>
                    <select
                      id='ubm-format'
                      className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm'
                      {...formik.getFieldProps('format')}>
                      <option value='image/png'>PNG</option>
                      <option value='image/jpeg'>JPEG</option>
                    </select>
                  </div>
                </div>
              </>
            )}
            {(formik.values.type === 'XYZ' || formik.values.type === 'WMS') && (
              <div className='grid gap-2'>
                <Label htmlFor='ubm-tilesize'>{t('viewerSettingsDialog.tileSizeLabel')}</Label>
                <select
                  id='ubm-tilesize'
                  className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm'
                  value={formik.values.tileSize}
                  onChange={(e) => formik.setFieldValue('tileSize', parseInt(e.target.value))}>
                  <option value={256}>256px</option>
                  <option value={512}>512px</option>
                </select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
              {t('viewerSettingsDialog.cancel')}
            </Button>
            <Button type='submit' disabled={!formik.values.name || !formik.values.url}>
              {baseMap ? t('viewerSettingsDialog.save') : t('viewerSettingsDialog.addBaseMap')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
